import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../firebase/AuthProvider';
import { resourceChat, resourceIdsInDoc, ResourceChatPreview } from '../../../services/resources/ResourceChatService';
import { useChatConnection } from '../../../contexts/ChatConnectionContext';
import ChatReferenceChip from './ChatReferenceChip';
import './ResourceLinkPreviews.css';

export default function ResourceLinkPreviews({ doc, appearance }: { doc: unknown; appearance?: 'dark' }) {
  const { currentUser, currentUserInfo, currentOrganization, currentRoles, impersonatedUserId } = useAuth();
  const { connection, connectionVersion } = useChatConnection();
  const idsKey = resourceIdsInDoc(doc).join(',');
  const viewerKey = JSON.stringify([currentUser?.uid, currentUserInfo?.id, currentUserInfo?.organization?.id,
    currentOrganization?.name, currentOrganization?.abbreviation, (currentRoles ?? []).map(role => role.name).sort(), impersonatedUserId]);
  // Bootstrap organization metadata has no ID. Its owning context reference
  // complements the authoritative employee organization ID in viewerKey.
  const viewerScope = useMemo(() => ({ viewerKey, currentOrganization }), [viewerKey, currentOrganization]);
  const [rowsViewer, setRowsViewer] = useState<typeof viewerScope | null>(null);
  const [rows, setRows] = useState<Record<string, ResourceChatPreview | null>>({});
  useEffect(() => {
    const controller = new AbortController(); let sequence = 0; setRows({});
    const ids = idsKey ? idsKey.split(',') : [];
    const refresh = async () => {
      const currentSequence = ++sequence;
      if (!currentUser || document.visibilityState === 'hidden') { setRows({}); return; }
      try {
        const token = await currentUser.getIdToken();
        const pairs = await Promise.all(ids.map(async id => {
          try { return [id, await resourceChat.preview(token, id, controller.signal)] as const; }
          catch { return [id, null] as const; }
        }));
        if (!controller.signal.aborted && sequence === currentSequence) { setRows(Object.fromEntries(pairs)); setRowsViewer(viewerScope); }
      } catch { if (!controller.signal.aborted) setRows({}); }
    };
    if (ids.length) void refresh();
    const timer = ids.length ? window.setInterval(() => void refresh(), 30000) : undefined;
    window.addEventListener('focus', refresh); document.addEventListener('visibilitychange', refresh);
    window.addEventListener('resource-directory-changed', refresh);
    connection?.on('ResourceDirectoryChanged', refresh);
    return () => { controller.abort(); ++sequence; window.clearInterval(timer); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); window.removeEventListener('resource-directory-changed', refresh); connection?.off('ResourceDirectoryChanged', refresh); };
  }, [idsKey, currentUser, viewerScope, connection, connectionVersion]);
  if (!idsKey) return null;
  return <div className={`resource-reference-chips${appearance === 'dark' ? ' resource-reference-chips--dark' : ''}`} aria-label="Linked resources">
    {idsKey.split(',').map(id => {
      const row = rowsViewer === viewerScope ? rows[id] : null;
      return row ? <ChatReferenceChip key={`${viewerKey}:${id}`} href={`/resources/${id}`} title={row.title} kind="Resource" appearance={appearance} preview={<span className="resource-reference-tooltip-content">
        <strong>{row.title}</strong>{row.providerName.trim().toLowerCase() !== row.title.trim().toLowerCase() && <span>{row.providerName}</span>}
        {row.city && <span>{row.city}</span>}
        <span>Service states: {row.stateCodes.join(', ')}</span>
        {row.phone && <span>{row.phone}</span>}
        <span className="resource-reference-tooltip-action">Open resource in Waivera</span>
      </span>} /> : <span key={id} className="resource-reference-unavailable" role="status">{rowsViewer === viewerScope && Object.prototype.hasOwnProperty.call(rows, id) ? 'Resource unavailable' : 'Checking resource access…'}</span>;
    })}
  </div>;
}
